import React, { useRef, useState } from "react";
import scannedMediaObjectListStyle from "./scannedMediaObjectListStyle";
import LegalBirdPageLoading from "../ContentLoader/LegalBirdPageLoading";
import { Alert, Grid, LinearProgress, MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import ContentBox from "../ContentBox/ContentBox";
import _ from "lodash";
import ScannedMediaObjectRow from "./ScannedMediaObjectRow";
import ButtonLoading from "../Button/ButtonLoading";
import ActionIdentifierProvider, { useActionIdentifier } from "../../provider/ActionIdentifierProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { apiPost } from "../../services/Api/apiCall";
import DocumentClassLabelAiProvider, { useDocumentClassLabelAi } from "../../provider/DocumentClassLabelAiProvider";
import { useBackofficeUser } from "../../provider/BackofficeUserProvider";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService";
import { Roles } from "../../types/BackofficeUser";
import { fetchScannedMediaObjects, scannedMediaObjectsFilters } from "../../services/mediaObjectService";
import LabeledSelect from "../Select/LabeledSelect";
import { MediaObject } from "../../types/MediaObject";

const sources = [
  { value: "bea", label: "beA", fieldnames: ["bea", "beaAttachmentMerge", "beaGeneratedMessage"] },
  { value: "email", label: "Mail", fieldnames: ["emailAttachmentMerge"] },
  { value: "fax", label: "Fax", fieldnames: ["fax"] },
  { value: "scan", label: "Scan", fieldnames: ["scan"] },
];

export default function ScannedMediaObjectList() {
  return (
    <DocumentClassLabelAiProvider>
      <ActionIdentifierProvider>
        <ScannedMediaObjectListContent />
      </ActionIdentifierProvider>
    </DocumentClassLabelAiProvider>
  );
}

function ScannedMediaObjectListContent() {
  const [isLoading, setIsLoading] = useState(false);
  const [accountManagerFilter, setAccountManagerFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const { backofficeUsers } = useBackofficeUser();
  const { isLoading: isLoadingDocumentClassLabels } = useDocumentClassLabelAi();
  const { isLoading: isLoadingActionIdentifiers } = useActionIdentifier();
  const queryClient = useQueryClient();
  const refreshExistingMediaObjects = useRef(false);

  const { data: allMediaObjects, isError, isFetching } = useQuery({
    queryKey: [...queryKeys.collection("media_objects", scannedMediaObjectsFilters), "allPages"],
    queryFn: async ({ signal, queryKey }) => {
      const refreshExisting = refreshExistingMediaObjects.current;
      const previousMediaObjects = queryClient.getQueryData<MediaObject[]>(queryKey) ?? [];
      const fetchedMediaObjects = await fetchScannedMediaObjects(signal);
      const mediaObjectsById = new Map(previousMediaObjects.map((mediaObject) => [mediaObject.id, mediaObject]));
      const fetchedById = new Map(fetchedMediaObjects.map((mediaObject) => [mediaObject.id, mediaObject]));

      // Only an explicit refresh after an edit, deletion or confirmation updates existing entries.
      // Polling keeps their data and order intact and only appends new IDs.
      if (refreshExisting) {
        for (const id of mediaObjectsById.keys()) {
          const updatedMediaObject = fetchedById.get(id);
          if (updatedMediaObject) {
            mediaObjectsById.set(id, updatedMediaObject);
          } else {
            mediaObjectsById.delete(id);
          }
        }
      }
      for (const [id, mediaObject] of fetchedById) {
        if (!mediaObjectsById.has(id)) {
          mediaObjectsById.set(id, mediaObject);
        }
      }
      if (!signal.aborted) {
        refreshExistingMediaObjects.current = false;
      }
      return [...mediaObjectsById.values()];
    },
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  const source = sources.find((option) => option.value === sourceFilter);
  const mediaObjects = (allMediaObjects ?? []).filter((mediaObject) =>
    (accountManagerFilter === "all" || mediaObject.scanAccountManagerId === Number(accountManagerFilter)) &&
    (sourceFilter === "all" || source?.fieldnames.includes(mediaObject.fieldname ?? "")),
  );
  const accountManagers = _.sortBy(
    backofficeUsers.filter((user) => userHasOneOfTheseRoles(user, [Roles.accountmanager, Roles.leadAccountmanager])),
    (user) => user.person.fullname,
  );

  if (isError && !allMediaObjects) {
    return <Alert severity="error">Die Dokumente konnten nicht geladen werden.</Alert>;
  }

  if (!allMediaObjects || isLoadingDocumentClassLabels || isLoadingActionIdentifiers) {
    return <LegalBirdPageLoading type="spinner" />;
  }

  const refreshList = () => {
    refreshExistingMediaObjects.current = true;
    return queryClient.invalidateQueries({
      queryKey: queryKeys.collection("media_objects", scannedMediaObjectsFilters)
    });
  };

  const handleConfirmClick = async () => {
    setIsLoading(true);
    let checkedMediaObjects: number[] = [];
    _.forEach(mediaObjects, (mediaObject) => {
      if (mediaObject.scanMetaData?.checkedDate) {
        checkedMediaObjects.push(mediaObject.id);
      }
    });
    await apiPost("lb/services/checked_media_objects", checkedMediaObjects);
    await refreshList();
    setIsLoading(false);
  };

  return (
    <ContentBox>
      <Grid container spacing={2} sx={{ justifyContent: "flex-end", mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <LabeledSelect
            label="Nutzer"
            value={accountManagerFilter}
            onChange={({ target }) => setAccountManagerFilter(target.value)}
          >
            <MenuItem value="all">Alle Sachbearbeiter</MenuItem>
            {accountManagers.map((user) => (
              <MenuItem key={user.id} value={String(user.id)}>{user.person.fullname}</MenuItem>
            ))}
          </LabeledSelect>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <LabeledSelect
            label="Quelle"
            value={sourceFilter}
            onChange={({ target }) => setSourceFilter(target.value)}
          >
            <MenuItem value="all">Alle Quellen</MenuItem>
            {sources.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </LabeledSelect>
        </Grid>
      </Grid>
      <LinearProgress sx={{ visibility: isFetching ? "visible" : "hidden" }} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Datum</TableCell>
            <TableCell>Datei</TableCell>
            <TableCell>Fall</TableCell>
            <TableCell sx={scannedMediaObjectListStyle.searchColumn} width={"25%"}>
              Suche
            </TableCell>
            <TableCell sx={scannedMediaObjectListStyle.identifierColumn}>Dokumentenart</TableCell>
            <TableCell sx={scannedMediaObjectListStyle.identifierColumn}>Aktion</TableCell>
            <TableCell>Prüfen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mediaObjects.length < 1 ? (
            <TableRow>
              <TableCell colSpan={7} sx={scannedMediaObjectListStyle.centerText}>
                Keine zuzuordnenden Dateien
              </TableCell>
            </TableRow>
          ) : (
            _.map(mediaObjects, (mediaObject) => (
              <ScannedMediaObjectRow
                mediaObject={mediaObject}
                key={mediaObject.id}
                refreshList={refreshList}
              />
            ))
          )}
        </TableBody>
      </Table>
      <ButtonLoading
        variant={"contained"}
        sx={scannedMediaObjectListStyle.button}
        onClick={handleConfirmClick}
        isLoading={isLoading}
        disabled={!_.find(mediaObjects, (mediaObject) => mediaObject.scanMetaData?.checkedDate)}
      >
        Prüfung bestätigen
      </ButtonLoading>
    </ContentBox>
  );
}
